/* eslint-disable react/prop-types */
import logger from 'app/utils/debug/logger';
import React, { useMemo, useState } from 'react';
import { useCallback } from 'react';
import { Button } from 'semantic-ui-react';
import { useTimeout } from 'usehooks-ts';

type ActionResultButtonProps<T> = React.PropsWithChildren<
  {
    onClick: () => T | Promise<T>;
    content?: React.ReactNode;
    successContent?: React.ReactNode;
    failureContent?: React.ReactNode;
    onSuccess?: (result: T) => void;
    onFailure?: () => void;
    successProps?: React.ComponentProps<typeof Button>;
    failureProps?: React.ComponentProps<typeof Button>;
    delay?: number;
  } & React.ComponentProps<typeof Button>
>;

export default function ActionResultButton<T>({
  onClick,
  content = undefined,
  children,
  successContent,
  failureContent,
  onSuccess,
  onFailure,
  successProps = {},
  failureProps = {},
  delay = 1000,
  ...restButtonProps
}: ActionResultButtonProps<T>) {
  const [state, setState] = useState<undefined | 'success' | 'failure'>();
  const [result, setResult] = useState<undefined | T>();
  const defaultContent = content ?? children;
  const delayOrDisableTimeout = state !== undefined ? delay : null;

  useTimeout(() => {
    if (state === 'failure') onFailure?.();
    else onSuccess?.(result);

    setState(undefined);
  }, delayOrDisableTimeout);

  const onClickAction = useCallback(async () => {
    let clickResult;
    try {
      clickResult = await onClick();
      setResult(clickResult);
    } catch (e) {
      clickResult = false;
    }
    const isFailure = clickResult === false;
    setState(isFailure ? 'failure' : 'success');
  }, [onClick]);

  const [renderedContent, buttonProps] = useMemo(() => {
    switch (state) {
      case 'success':
        return [successContent ?? defaultContent, { ...restButtonProps, ...successProps }];
      case 'failure':
        return [failureContent ?? defaultContent, { ...restButtonProps, ...failureProps }];
      default:
        return [defaultContent, restButtonProps];
    }
  }, [state, defaultContent, failureContent, successContent]);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Button onClick={onClickAction} {...buttonProps}>
      {renderedContent}
    </Button>
  );
}
